/**
 * Version detection utility to notify users when a new deployment is available
 * Prevents chunk loading errors by prompting users to refresh
 */

import { Button } from '@components/primitives/Button';
import { enqueueSnackbar } from 'notistack';

import { APP_DEPLOY_VERSION } from '../config/appVersion';

interface VersionInfo {
  version: string;
  deployedAt: string;
  branch: string;
}

// State management for polling
let lastNotifiedVersion: string | null = null;
let isPolling = false;
let isChecking = false;

/**
 * Check if a new version of the app is available
 * @param signal - Optional AbortSignal to cancel the request
 * @returns Promise<VersionInfo | null> - version info if new version detected, null otherwise
 */
export async function checkForNewVersion(signal?: AbortSignal): Promise<VersionInfo | null> {
  try {
    // Add timestamp to bypass cache, include abort signal
    const response = await fetch(`/version.json?t=${Date.now()}`, { signal });

    if (!response.ok) {
      console.warn('Version check failed:', response.status);
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const data: VersionInfo = await response.json();

    // Compare versions
    if (data.version !== APP_DEPLOY_VERSION) {
      console.log('New version detected:', {
        current: APP_DEPLOY_VERSION,
        latest: data.version,
        deployedAt: data.deployedAt,
      });
      return data;
    }

    return null;
  } catch (error) {
    // Don't log abort errors - they're intentional
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Version check aborted');
      return null;
    }
    console.warn('Version check error:', error);
    return null;
  }
}

/**
 * Show a notification to the user about a new version
 * Uses notistack toast for non-intrusive notification
 * @param detectedVersion - The new version that was detected
 */
export function showUpdateNotification(detectedVersion: string) {
  // Only notify if we haven't already notified for this specific version
  if (lastNotifiedVersion === detectedVersion) return;
  lastNotifiedVersion = detectedVersion;

  enqueueSnackbar('App update available!', {
    variant: 'default',
    persist: true,
    preventDuplicate: true,
    action: () => (
      <Button
        variant="contained"
        size="small"
        onClick={() => window.location.reload()}
        sx={{ ml: 2 }}
      >
        Reload
      </Button>
    ),
  });
}

/**
 * Start periodic version checking
 * @param intervalMs - Check interval in milliseconds (default: 5 minutes)
 */
export function startVersionPolling(intervalMs: number = 5 * 60 * 1000) {
  if (isPolling) {
    console.warn('Version polling already started');
    return () => {}; // Return no-op cleanup
  }

  isPolling = true;
  let intervalId: NodeJS.Timeout | null = null;
  let abortController: AbortController | null = new AbortController();

  const checkAndNotify = async (): Promise<boolean> => {
    // Prevent concurrent checks
    if (isChecking || !abortController) {
      console.log('Version check already in progress, skipping...');
      return false;
    }

    isChecking = true;
    try {
      const versionInfo = await checkForNewVersion(abortController.signal);
      if (versionInfo) {
        showUpdateNotification(versionInfo.version);
        return true; // Stop polling
      }
      return false;
    } finally {
      isChecking = false;
    }
  };

  // Wait for initial check to complete before starting interval
  checkAndNotify().then(shouldStop => {
    if (!shouldStop && isPolling) {
      intervalId = setInterval(() => {
        checkAndNotify().then(shouldStop => {
          if (shouldStop && intervalId) {
            clearInterval(intervalId);
            intervalId = null;
            isPolling = false;
          }
        });
      }, intervalMs);
    } else {
      isPolling = false;
    }
  });

  // Cleanup function
  return () => {
    isPolling = false;

    // Cancel any in-flight requests
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    // Clear interval
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
}

/**
 * Get the current app version
 */
export function getCurrentVersion(): string {
  return APP_DEPLOY_VERSION;
}
